import React from 'react';
import PropTypes from 'prop-types';
import styles from 'components/common/CustomConfirmModal/CustomConfirmModal.module.css';

// Shared preflight dialog rendered after a lookup-by-email returns Exists=true.
// Decides whether to BLOCK (with a specific reason) or CONFIRM (with the right copy),
// based on the lookup result + the purpose of the add action.
//
// 'secondary' and 'member' are wired up; 'guest' reuses the member evaluation (a guest is added the same way, just
// at the Guest role — the confirm copy reflects the chosen role).
//
// When lookupResult.exists === false the caller should not render this — fall through
// to the existing create-new-user POST instead.

const namesDiffer = (typedFirstName, typedLastName, onRecordFirst, onRecordLast) => {
  const t = `${(typedFirstName || '').trim()} ${(typedLastName || '').trim()}`
    .trim()
    .toLowerCase();
  const o = `${(onRecordFirst || '').trim()} ${(onRecordLast || '').trim()}`
    .trim()
    .toLowerCase();
  return t.length > 0 && t !== o;
};

const evaluateSecondary = (lookupResult, context) => {
  const { userID, firstName, lastName, email, platformContactRole, thisClubMembership } =
    lookupResult;
  const { primaryContactId, typedFirstName, typedLastName } = context;
  const onRecordName = `${firstName || ''} ${lastName || ''}`.trim() || email;

  // BLOCK 1: cannot be primary and secondary for the same family.
  if (userID === primaryContactId) {
    return {
      mode: 'block',
      title: 'Cannot add as secondary contact',
      body: (
        <>
          <p>
            <strong>{onRecordName}</strong> is the primary contact for this
            family. A user cannot be both the primary and secondary contact for
            the same family.
          </p>
        </>
      ),
    };
  }

  // BLOCK 2: already a primary contact for athletes anywhere on the platform. Not how many: the lookup says no more
  // about another family than it must (Doug, 2026-11-05).
  if (platformContactRole?.isPrimaryContact) {
    return {
      mode: 'block',
      title: 'Cannot add as secondary contact',
      body: (
        <>
          <p>
            <strong>{onRecordName}</strong> ({email}) is already a primary
            contact on SplitFast.
          </p>
          <p>
            The same person cannot be both a primary contact and a secondary
            contact — they must be one or the other across the whole platform.
          </p>
          <p style={{ color: '#64748b', fontSize: '0.9em' }}>
            To proceed, the primary-contact link for their athletes would need
            to be reassigned to someone else first.
          </p>
        </>
      ),
    };
  }

  // BLOCK 3: already a secondary contact for a different family. The server identifies the family only when it is
  // this one (a re-add); another family is never named (Doug, 2026-11-05).
  if (
    platformContactRole?.isSecondaryContact &&
    platformContactRole.secondaryForFamilyPrimaryUserID !== primaryContactId
  ) {
    return {
      mode: 'block',
      title: 'Cannot add as secondary contact',
      body: (
        <>
          <p>
            <strong>{onRecordName}</strong> ({email}) is already the secondary
            contact for another family.
          </p>
          <p>
            A user can only be the secondary contact for one family at a time.
          </p>
          <p style={{ color: '#64748b', fontSize: '0.9em' }}>
            To proceed, unlink them from that family first.
          </p>
        </>
      ),
    };
  }

  // CONFIRM: the add is allowed; surface what's about to happen.
  const nameMismatch = namesDiffer(
    typedFirstName,
    typedLastName,
    firstName,
    lastName
  );
  const willBeAddedToClub = !thisClubMembership;
  const isProtected = lookupResult.platformProtectedRole;

  return {
    mode: 'confirm',
    title: 'Existing SplitFast account',
    confirmLabel: `Add ${onRecordName}`,
    body: (
      <>
        <p>
          An account already exists for <strong>{email}</strong>:
        </p>
        <p style={{ fontSize: '1.05em' }}>
          <strong>{onRecordName}</strong>
        </p>
        {nameMismatch && (
          <p style={{ color: '#92400e', background: '#fffbeb', padding: '8px 12px', borderRadius: 6 }}>
            You entered <strong>{`${typedFirstName || ''} ${typedLastName || ''}`.trim()}</strong>.
            Continuing will use the on-record name above.
          </p>
        )}
        <p>
          They will become the secondary contact for
          this family — for every club these athletes belong to.
        </p>
        {willBeAddedToClub && (
          <p>
            They are not currently a member of this club. Adding them as the
            secondary contact will also add them to the club as a Member.
          </p>
        )}
        {thisClubMembership && (
          <p style={{ color: '#64748b', fontSize: '0.9em' }}>
            They are currently a {thisClubMembership.role} of this club.
          </p>
        )}
        {isProtected && (
          <p style={{ color: '#92400e', background: '#fffbeb', padding: '8px 12px', borderRadius: 6 }}>
            Note: this account is a SplitFast <strong>{isProtected}</strong>.
            Make sure this is the right person before proceeding.
          </p>
        )}
      </>
    ),
  };
};

const evaluateMember = (lookupResult, context) => {
  const {
    firstName,
    lastName,
    email,
    emailConfirmed,
    platformContactRole,
    thisClubMembership,
    platformProtectedRole,
  } = lookupResult;
  const { selectedRole, typedFirstName, typedLastName } = context;
  const onRecordName = `${firstName || ''} ${lastName || ''}`.trim() || email;

  // BLOCK: any existing membership in this club (active OR inactive) — the
  // backend rejects this at UsersController.cs:142-148 ("User is already a
  // member of the club"). Direct the admin to the existing profile rather
  // than telling them the system can't help; the existing edit forms support
  // changing role and participation status, which is how reactivation works
  // today (§5.4 reactivate is Phase 1 ergonomic shortcut, not new capability).
  if (thisClubMembership) {
    const isGuest = thisClubMembership.role === 'Guest';
    const isInactive = thisClubMembership.participationStatus === 'Inactive';
    const listName = isGuest ? 'Guest Members list' : 'Members list';
    return {
      mode: 'block',
      title: 'Already a member of this club',
      body: (
        <>
          <p>
            <strong>{onRecordName}</strong> ({email}) is already
            {isGuest ? ' a Guest member ' : ` a ${thisClubMembership.role} `}
            of your club
            {isInactive ? ' (currently Inactive).' : '.'}
          </p>
          <p>
            They can’t be re-added through this flow. Open their member
            profile from the <strong>{listName}</strong> to make any changes.
          </p>
          {isInactive && (
            <p style={{ color: '#64748b', fontSize: '0.9em' }}>
              To bring them back as active: edit their profile, set the
              membership status to <strong>Active</strong>, then reactivate their
              athletes from each athlete profile.
            </p>
          )}
        </>
      ),
    };
  }

  // CONFIRM: existing portal user, not currently in this club. The normal
  // add-existing-user-to-club case (also covers "re-add a removed member").
  const nameMismatch = namesDiffer(
    typedFirstName,
    typedLastName,
    firstName,
    lastName
  );
  const roleLabel = selectedRole || 'Member';
  const isPrimaryElsewhere = platformContactRole?.isPrimaryContact;
  const isSecondaryElsewhere = platformContactRole?.isSecondaryContact;

  return {
    mode: 'confirm',
    title: 'Existing SplitFast account',
    confirmLabel: `Add ${onRecordName}`,
    body: (
      <>
        <p>
          An account already exists for <strong>{email}</strong>:
        </p>
        <p style={{ fontSize: '1.05em' }}>
          <strong>{onRecordName}</strong>
        </p>
        {nameMismatch && (
          <p style={{ color: '#92400e', background: '#fffbeb', padding: '8px 12px', borderRadius: 6 }}>
            You entered <strong>{`${typedFirstName || ''} ${typedLastName || ''}`.trim()}</strong>.
            Continuing will use the on-record name above.
          </p>
        )}
        <p>
          They will be added to this club as a <strong>{roleLabel}</strong>.
          Their platform account and any membership they have in other clubs
          are preserved.
        </p>
        {emailConfirmed ? (
          <p style={{ color: '#166534', background: '#ecfdf3', padding: '8px 12px', borderRadius: 6 }}>
            They already have portal access — they’ll be added with full
            access; no invitation needed.
          </p>
        ) : (
          <p style={{ color: '#92400e', background: '#fffbeb', padding: '8px 12px', borderRadius: 6 }}>
            They have an account but haven’t completed portal onboarding. After
            adding them, you can send an invitation from the member list.
          </p>
        )}
        {isPrimaryElsewhere && (
          <p style={{ color: '#64748b', fontSize: '0.9em' }}>
            They are already a primary contact on SplitFast — their athletes
            won’t be added to your club automatically.
            After adding {firstName || 'this member'}, open their profile to add
            any of those athletes (or new ones) to your club.
          </p>
        )}
        {isSecondaryElsewhere && (
          <p style={{ color: '#64748b', fontSize: '0.9em' }}>
            They are currently the secondary contact for another family — that
            is unchanged by adding them here.
          </p>
        )}
        {platformProtectedRole && (
          <p style={{ color: '#92400e', background: '#fffbeb', padding: '8px 12px', borderRadius: 6 }}>
            Note: this account is a SplitFast{' '}
            <strong>{platformProtectedRole}</strong>. Make sure this is the
            right person before proceeding.
          </p>
        )}
      </>
    ),
  };
};

const UserLookupConfirmDialog = ({
  show,
  lookupResult,
  purpose,
  context,
  loading,
  onConfirm,
  onCancel,
}) => {
  if (!show || !lookupResult || !lookupResult.exists) {
    return null;
  }

  let decision;
  if (purpose === 'secondary') {
    decision = evaluateSecondary(lookupResult, context);
  } else if (purpose === 'member' || purpose === 'guest') {
    decision = evaluateMember(lookupResult, context);
  } else {
    return null;
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{decision.title}</h2>
          <button
            className={styles.closeBtn}
            onClick={onCancel}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <div className={styles.modalBody}>{decision.body}</div>
        <div className={styles.modalFooter}>
          {decision.mode === 'block' ? (
            <button className={styles.btnPrimary} onClick={onCancel}>
              Close
            </button>
          ) : (
            <>
              <button
                className={styles.btnSecondary}
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className={styles.btnPrimary}
                onClick={onConfirm}
                disabled={loading}
              >
                {loading ? 'Processing...' : decision.confirmLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

UserLookupConfirmDialog.propTypes = {
  show: PropTypes.bool.isRequired,
  lookupResult: PropTypes.object,
  purpose: PropTypes.oneOf(['secondary', 'member', 'guest']).isRequired,
  context: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default UserLookupConfirmDialog;
